import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Printer, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { SESSION_KEY } from '../helper/common';
import axios from 'axios';

const config = {
  apiUrl: process.env.REACT_APP_API_URL_User,
  currency: 'Rs',
};

const TaxCertificate = () => {
  const [certificateData, setCertificateData] = useState({
    CNIC: 'No record Found',
    CSTMTXN_CUST_ID: 'No record Found',
    CUST_NAME: 'No record Found',
    TAX_DEDUCTED: 'No record Found',
    CARD_NO: 'No record Found',
    FROM_DATE: '',
    TO_DATE: '',
  });
  const [fromDate, setFromDate] = useState(localStorage.getItem('fromDate') || '2025-01-01');
  const [toDate, setToDate] = useState(localStorage.getItem('toDate') || '2025-12-31');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const navigate = useNavigate();


  const custId = localStorage.getItem('custId');
  const cardNo = localStorage.getItem('cardNumber');

  React.useEffect(() => {
    if (!custId || !cardNo) {
      toast.error('Please log in to view the tax certificate.');
      navigate('/login');
    }
  }, [custId, cardNo, navigate]);

  const maskCardNumber = (cardNo) => {
    if (!cardNo || cardNo.length < 16) return 'Invalid Card Number';
    return `XXXX-XXXX-XXXX-${cardNo.slice(-4)}`;
  };

  const validateDates = () => {
    if (!fromDate || !toDate) {
      setError('Please select both From and To dates.');
      toast.error('Please select both From and To dates.');
      return false;
    }
    const from = new Date(fromDate);
    const to = new Date(toDate);
    if (from > to) {
      setError('From Date must be earlier than To Date.');
      toast.error('From Date must be earlier than To Date.');
      return false;
    }
    return true;
  };

  const handleGenerateCertificate = async (e) => {
    e.preventDefault();
    if (!validateDates()) return;

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        custId: custId,
        fromDate: fromDate.replace(/-/g, ''),
        toDate: toDate.replace(/-/g, ''),
        cardNo: cardNo,
      };

      const token = localStorage.getItem(SESSION_KEY);

      // Set responseType to 'blob' to handle binary PDF data
      const response = await axios.post(`${config.apiUrl}/api/taxcertificate`, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });

      // Create a blob from the PDF data
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // Create a link element and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Tax_Certificate_${fromDate}_to_${toDate}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess('Tax certificate downloaded successfully!');
      toast.success('Tax certificate downloaded successfully!');

    } catch (err) {
      console.error('Error fetching tax certificate:', err);
      setError('Error fetching tax certificate data.');
      toast.error('Error fetching tax certificate data.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-slate-50 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-4 font-['Inter'] bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-400 dark:to-blue-500 bg-clip-text text-transparent">
            Tax Certificate
          </h2>
          <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto text-lg">
            Select the period to generate and print your tax certificate.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-slate-200/50 hover:shadow-xl hover:bg-white/90 transition-all duration-300 dark:bg-gray-800/80 dark:border-gray-700/50 dark:hover:bg-gray-800/90 mb-8"
        >
          <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-6">Select Date Range</h3>
          <form onSubmit={handleGenerateCertificate} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
              >
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  From Date
                </label>
                <input
                  type="date"
                  value={fromDate}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full border border-slate-300 dark:border-gray-600 rounded-lg p-2 text-slate-900 dark:text-white bg-white dark:bg-gray-800 focus:ring-blue-700 dark:focus:ring-blue-400"
                  required
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
              >
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  To Date
                </label>
                <input
                  type="date"
                  value={toDate}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full border border-slate-300 dark:border-gray-600 rounded-lg p-2 text-slate-900 dark:text-white bg-white dark:bg-gray-800 focus:ring-blue-700 dark:focus:ring-blue-400"
                  required
                />
              </motion.div>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-400 dark:to-blue-500 text-white py-3 px-4 rounded-xl font-medium hover:from-blue-700 hover:to-blue-800 dark:hover:from-blue-500 dark:hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-700 dark:focus:ring-blue-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Generating...</span>
                </div>
              ) : (
                <span>Generate Tax Certificate</span>
              )}
            </button>
          </form>
        </motion.div>

        {isLoading && (
          <div className="mb-4 p-4 bg-blue-100 text-blue-700 rounded-lg flex items-center justify-center">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            <span>Loading tax certificate...</span>
          </div>
        )}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center"
          >
            <AlertCircle className="h-5 w-5 mr-2" />
            <span>{error}</span>
          </motion.div>
        )}
        {success && !isLoading && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg flex items-center"
          >
            <span>{success}</span>
          </motion.div>
        )}

        {success && !isLoading && certificateData.CNIC !== 'No record Found' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-slate-200/50 hover:shadow-xl hover:bg-white/90 transition-all duration-300 dark:bg-gray-800/80 dark:border-gray-700/50 dark:hover:bg-gray-800/90"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Tax Certificate Details</h3>
              <button
                onClick={handlePrint}
                className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </button>
            </div>

            <div id="printArea" className="w-full text-sm">
              <img
                src="/images/mcb-logo.png"
                alt="MCB Logo"
                className="mb-6"
                style={{ width: '80px', height: '80px' }}
              />
              <table className="w-full text-sm">
                <tbody>
                  <tr>
                    <td className="w-1/4 p-2 font-bold text-left">Serial Number</td>
                    <td className="w-3/4 p-2 text-center"></td>
                  </tr>
                  <tr>
                    <td className="w-1/4 p-2 font-bold text-left">Credit Card Number</td>
                    <td className="w-3/4 p-2 text-center">
                      <u>{certificateData.CARD_NO}</u>
                    </td>
                  </tr>
                  <tr>
                    <td className="w-1/4 p-2 font-bold text-left">
                      Certified that the Sum of Rupees on account of Income Tax has been deducted/collected from
                    </td>
                    <td className="w-3/4 p-2 text-center">
                      <u>{certificateData.TAX_DEDUCTED}</u>
                    </td>
                  </tr>
                  <tr>
                    <td className="w-1/4 p-2 font-bold text-left">Name</td>
                    <td className="w-3/4 p-2 text-center">
                      <u>{certificateData.CUST_NAME}</u>
                    </td>
                  </tr>
                  <tr>
                    <td className="w-1/4 p-2 font-bold text-left">NTN</td>
                    <td className="w-3/4 p-2 text-center">
                      <u></u>
                    </td>
                  </tr>
                  <tr>
                    <td className="w-1/4 p-2 font-bold text-left">CNIC</td>
                    <td className="w-3/4 p-2 text-center">
                      <u>{certificateData.CNIC}</u>
                    </td>
                  </tr>
                  <tr>
                    <td className="w-1/4 p-2 font-bold text-left">During the period</td>
                    <td className="w-3/4 p-2 text-center">
                      <u>{`${certificateData.FROM_DATE} To ${certificateData.TO_DATE}`}</u>
                    </td>
                  </tr>
                  <tr>
                    <td className="w-1/4 p-2 font-bold text-left">Under section</td>
                    <td className="w-3/4 p-2 text-center">
                      <u>236Y of the Income Tax Ordinance 2001</u>
                    </td>
                  </tr>
                  <tr>
                    <td className="w-1/4 p-2 font-bold text-left">On Account of</td>
                    <td className="w-3/4 p-2 text-center">
                      <u>Foreign transaction charges</u>
                    </td>
                  </tr>
                  <tr>
                    <td className="w-1/4 p-2 font-bold text-left">On the value/amount of Rupee</td>
                    <td className="w-3/4 p-2 text-center"></td>
                  </tr>
                </tbody>
              </table>
              <div className="mt-6">
                <img
                  src="/images/tax_page.png"
                  alt="Tax Page Background"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </motion.div>
        )}
      </div>

      <style jsx>{`
        @media print {
          body {
            visibility: hidden;
            background-color: #fff !important;
          }
          #printArea {
            visibility: visible;
            position: absolute;
            left: 0;
            top: 0;
            background-color: #fff !important;
          }
          table {
            font-size: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default TaxCertificate;